import { Component, inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TRAFFIC_SOURCES } from '@app/_shared/_constants/traffic-source';
import { safeSessionStorageSetItem } from '@app/_shared/_helpers/platform.helpers';
import { CartService, CustomerService } from '@app/_shared/_services';
import { filter, startWith, map } from 'rxjs';
import { SidenavService } from './sidenav/sidenav.service';
import { FooterComponent } from './footer/footer.component';
import { NavbarComponent } from './navbar/navbar.component';
import { SidenavComponent } from './sidenav/sidenav.component';
import { register } from 'swiper/element/bundle'

@Component({
  selector: 'app-root',
  imports: [SidenavComponent, NavbarComponent, FooterComponent, RouterOutlet],
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.scss'
})
export class AppShellComponent implements OnInit {
  private readonly cartService = inject(CartService);
  private readonly router = inject(Router);
  private readonly sidenavService = inject(SidenavService);
  private readonly titleService = inject(Title);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly customerService = inject(CustomerService);

  private readonly title = 'My Pool Hut';

  constructor() {
    // Register Swiper only one time
    register();
  }

  public async ngOnInit(): Promise<void> {
    const appTitle = this.titleService.getTitle();

    // Capture the traffic source synchronously from the landing URL before any
    // async work, so query params can't be lost to router timing/navigation.
    this.captureTrafficSource();

    this.cartService.getCart();
    await this.customerService.loginCustomerFromStorage();

    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        startWith(this.router),
        map(() => {
          let child = this.activatedRoute.firstChild;
          while (child?.firstChild) {
            child = child.firstChild;
          }
          if (child?.snapshot?.data['title']) {
            return `${child?.snapshot?.data['title']} | ${this.title}`;
          }

          return `${appTitle}`;
        })
      )
      .subscribe((ttl: string) => {
        this.titleService.setTitle(ttl);
        this.sidenavService.close();
      });
  }

  private captureTrafficSource(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);

    if (params.get(TRAFFIC_SOURCES.GOOGLE.id)) {
      safeSessionStorageSetItem('trafficSource', TRAFFIC_SOURCES.GOOGLE.label);
    } else if (params.get(TRAFFIC_SOURCES.FACEBOOK.id)) {
      safeSessionStorageSetItem('trafficSource', TRAFFIC_SOURCES.FACEBOOK.label);
    } else if (params.get(TRAFFIC_SOURCES.TIKTOK.id)) {
      safeSessionStorageSetItem('trafficSource', TRAFFIC_SOURCES.TIKTOK.label);
    } else if (params.get(TRAFFIC_SOURCES.PINTEREST.id)) {
      safeSessionStorageSetItem('trafficSource', TRAFFIC_SOURCES.PINTEREST.label);
    } else if (params.get(TRAFFIC_SOURCES.MICROSOFT.id)) {
      safeSessionStorageSetItem('trafficSource', TRAFFIC_SOURCES.MICROSOFT.label);
    } else if (params.get(TRAFFIC_SOURCES.SNAPCHAT.id)) {
      safeSessionStorageSetItem('trafficSource', TRAFFIC_SOURCES.SNAPCHAT.label);
    } else if (params.get(TRAFFIC_SOURCES.TWITTER.id)) {
      safeSessionStorageSetItem('trafficSource', TRAFFIC_SOURCES.TWITTER.label);
    } else if (params.get(TRAFFIC_SOURCES.GOOGLE_MERCHANT.id)) {
      safeSessionStorageSetItem('trafficSource', TRAFFIC_SOURCES.GOOGLE_MERCHANT.label);
    } else if (
      // Organic ChatGPT links use utm_source=chatgpt.com without utm_medium=cpc, so they stay Organic.
      params.get(TRAFFIC_SOURCES.CHATGPT.id)?.toLowerCase()
        .startsWith('chatgpt') &&
      params.get('utm_medium')?.toLowerCase() === 'cpc'
    ) {
      safeSessionStorageSetItem('trafficSource', TRAFFIC_SOURCES.CHATGPT.label);
    } else if (params.get(TRAFFIC_SOURCES.EMAIL.id) === 'email') {
      safeSessionStorageSetItem('trafficSource', `${TRAFFIC_SOURCES.EMAIL.label} - ${params.get('utm_campaign')}`);
    } else {
      // Click IDs are stripped by some browsers/extensions (Firefox ETP, Brave,
      // uBlock, etc.) — fall back to the referrer to identify the source.
      this.captureTrafficSourceFromReferrer();
    }
  }

  private captureTrafficSourceFromReferrer(): void {
    if (!document.referrer) {
      return;
    }

    let host: string;
    try {
      host = new URL(document.referrer).hostname.toLowerCase();
    } catch {
      return;
    }

    const isFrom = (domain: string): boolean => host === domain || host.endsWith(`.${domain}`);

    if (isFrom('facebook.com') || isFrom('fb.com')) {
      safeSessionStorageSetItem('trafficSource', TRAFFIC_SOURCES.FACEBOOK.label);
    } else if (isFrom('tiktok.com')) {
      safeSessionStorageSetItem('trafficSource', TRAFFIC_SOURCES.TIKTOK.label);
    } else if (isFrom('pinterest.com')) {
      safeSessionStorageSetItem('trafficSource', TRAFFIC_SOURCES.PINTEREST.label);
    } else if (isFrom('snapchat.com')) {
      safeSessionStorageSetItem('trafficSource', TRAFFIC_SOURCES.SNAPCHAT.label);
    } else if (isFrom('twitter.com') || isFrom('x.com') || isFrom('t.co')) {
      safeSessionStorageSetItem('trafficSource', TRAFFIC_SOURCES.TWITTER.label);
    }
  }
}
